import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  Table,
  message
} from "antd";
import {
  createSprKassa,
  fetchSprKassa,
  fetchSprKassaBranches,
  saveSprKassa
} from "./spr-kassa-api";
import {
  SNO_OPTIONS,
  VID_KKM_ONLINE_SBERBANK,
  VID_KKM_OPTIONS,
  VID_KKM_SD_UDS,
  normalizeBranch,
  normalizeKassa,
  packDeps,
  unpackDeps
} from "./spr-kassa-utils";

const LABEL_WIDTH = 160;

const formItemLayout = {
  labelCol: { flex: `0 0 ${LABEL_WIDTH}px` },
  wrapperCol: { flex: "1 1 auto" },
  labelAlign: "right"
};

const TAX_OPTIONS = Array.from({ length: 16 }, (_, i) => ({
  value: i,
  label: String(i)
}));

/**
 * Форма карточки кассы (Delphi TFrmESprKassa).
 */
const SprKassaMain = ({ idKassa, isNew, open, canEdit, onSaved, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [branches, setBranches] = useState([]);
  const [deps, setDeps] = useState(() => unpackDeps("", ""));

  const vid = Form.useWatch("vid", form) ?? 0;
  const isUds = vid === VID_KKM_SD_UDS;
  const isSber = vid === VID_KKM_ONLINE_SBERBANK;
  const sdEnabled = isUds || isSber;
  const urlsEnabled = isSber;

  const sdUserLabel = isUds ? "ID компании" : "Пользователь";
  const sdPassLabel = isUds ? "API key" : "Пароль";

  const branchOptions = useMemo(
    () => [{ value: 0, label: "—" }, ...branches.map((b) => ({ value: b.id, label: b.name || `#${b.id}` }))],
    [branches]
  );

  useEffect(() => {
    if (!open) return;
    setError("");

    let cancelled = false;
    fetchSprKassaBranches()
      .then((data) => {
        if (cancelled) return;
        const rows = Array.isArray(data) ? data.map(normalizeBranch).filter((b) => b.id != null) : [];
        setBranches(rows);
      })
      .catch(() => {
        if (!cancelled) setBranches([]);
      });

    if (isNew) {
      form.resetFields();
      form.setFieldsValue({
        name: "",
        kassirName: "",
        prc: 0,
        sn: "",
        isHide: false,
        isTerm: false,
        vid: 0,
        idBranch: 0,
        sdUser: "",
        sdPassword: "",
        sdFinishUrl: "",
        sdFailUrl: ""
      });
      setDeps(unpackDeps("", ""));
      return () => {
        cancelled = true;
      };
    }

    if (idKassa == null) return undefined;

    setLoading(true);
    fetchSprKassa(idKassa)
      .then((data) => {
        if (cancelled) return;
        const row = normalizeKassa(data);
        if (row.idKassa == null) {
          setError("Касса не найдена");
          return;
        }
        form.setFieldsValue({
          idKassa: row.idKassa,
          name: row.name,
          kassirName: row.kassirName,
          prc: row.prc,
          sn: row.sn,
          isHide: row.isHide === 1,
          isTerm: row.isTerm === 1,
          vid: row.vid,
          idBranch: row.idBranch,
          sdUser: row.sdUser,
          sdPassword: row.sdPassword,
          sdFinishUrl: row.sdFinishUrl,
          sdFailUrl: row.sdFailUrl
        });
        setDeps(unpackDeps(row.taxes, row.sno));
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || "Не удалось загрузить");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isNew, idKassa, form]);

  const onDepChange = (depno, field, value) => {
    setDeps((prev) =>
      prev.map((row) => (row.depno === depno ? { ...row, [field]: value } : row))
    );
  };

  const onFinish = async (values) => {
    if (!canEdit) return;
    setSaving(true);
    setError("");
    const packed = packDeps(deps);
    const all = form.getFieldsValue(true);
    const body = {
      name: (values.name || "").trim(),
      kassirName: (values.kassirName || "").trim() || null,
      prc: values.prc ?? 0,
      sn: (values.sn || "").trim() || null,
      isHide: values.isHide ? 1 : 0,
      isTerm: values.isTerm ? 1 : 0,
      vid: values.vid ?? 0,
      idBranch: values.idBranch ?? 0,
      taxes: packed.taxes,
      sno: packed.sno,
      sdUser: (all.sdUser || "").trim() || null,
      sdPassword: (all.sdPassword || "").trim() || null,
      sdFinishUrl: (all.sdFinishUrl || "").trim() || null,
      sdFailUrl: (all.sdFailUrl || "").trim() || null
    };
    try {
      const saved = isNew
        ? await createSprKassa(body)
        : await saveSprKassa({ ...body, idKassa });
      message.success("Сохранено");
      onSaved?.(saved);
    } catch (e) {
      setError(e.message || "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  const depColumns = [
    {
      title: "Отдел",
      dataIndex: "depno",
      key: "depno",
      width: 72,
      align: "right"
    },
    {
      title: "Код налога",
      dataIndex: "tax",
      key: "tax",
      width: 110,
      render: (tax, row) => (
        <Select
          size="small"
          style={{ width: "100%" }}
          value={tax}
          options={TAX_OPTIONS}
          disabled={!canEdit}
          onChange={(v) => onDepChange(row.depno, "tax", v)}
        />
      )
    },
    {
      title: "Система налогообложения",
      dataIndex: "sno",
      key: "sno",
      render: (sno, row) => (
        <Select
          size="small"
          style={{ width: "100%" }}
          value={sno}
          options={SNO_OPTIONS}
          disabled={!canEdit}
          onChange={(v) => onDepChange(row.depno, "sno", v)}
        />
      )
    }
  ];

  if (loading) {
    return (
      <div className="spr-kassa-card__center">
        <Spin />
      </div>
    );
  }

  return (
    <div className="spr-kassa-card">
      <div className="spr-kassa-card__body">
        {error ? (
          <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />
        ) : null}
        <Form
          form={form}
          className="spr-kassa-card__form"
          {...formItemLayout}
          colon={false}
          onFinish={onFinish}
          requiredMark={false}
          disabled={!canEdit}
        >
          {!isNew ? (
            <Form.Item label="Код" name="idKassa">
              <Input disabled />
            </Form.Item>
          ) : null}
          <Form.Item label="Наименование" name="name">
            <Input maxLength={80} autoFocus={isNew || canEdit} />
          </Form.Item>
          <Form.Item label="ФИО кассира" name="kassirName">
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item label="% комиссии" name="prc">
            <InputNumber style={{ width: "100%" }} min={0} max={100} step={0.01} />
          </Form.Item>
          <Form.Item label="Серийный номер" name="sn">
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item label=" " colon={false} className="spr-kassa-card__checkboxes">
            <div className="spr-kassa-card__checkboxes-inner">
              <Form.Item name="isHide" valuePropName="checked" noStyle>
                <Checkbox>Скрыть</Checkbox>
              </Form.Item>
              <Form.Item name="isTerm" valuePropName="checked" noStyle>
                <Checkbox>Терминал оплаты</Checkbox>
              </Form.Item>
            </div>
          </Form.Item>
          <Form.Item label="Филиал" name="idBranch">
            <Select options={branchOptions} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item label="Вид ККМ" name="vid">
            <Select options={VID_KKM_OPTIONS} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item label={sdUserLabel} name="sdUser">
            <Input maxLength={80} disabled={!canEdit || !sdEnabled} />
          </Form.Item>
          <Form.Item label={sdPassLabel} name="sdPassword">
            <Input.Password maxLength={80} disabled={!canEdit || !sdEnabled} visibilityToggle />
          </Form.Item>
          <Form.Item label="URL успеха" name="sdFinishUrl">
            <Input maxLength={255} disabled={!canEdit || !urlsEnabled} />
          </Form.Item>
          <Form.Item label="URL ошибки" name="sdFailUrl">
            <Input maxLength={80} disabled={!canEdit || !urlsEnabled} />
          </Form.Item>
        </Form>

        <div className="spr-kassa-card__deps">
          <div className="spr-kassa-card__deps-title">Отделы</div>
          <Table
            size="small"
            rowKey="depno"
            pagination={false}
            columns={depColumns}
            dataSource={deps}
          />
        </div>
      </div>
      <div className="spr-kassa-card__footer">
        {canEdit ? (
          <Button type="primary" loading={saving} onClick={() => form.submit()}>
            Сохранить
          </Button>
        ) : null}
        <Button onClick={onCancel} disabled={saving}>
          Отмена
        </Button>
      </div>
    </div>
  );
};

export default SprKassaMain;
